import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabase';
import TermsModal from '../components/TermsModal';
import { useSiteLang as useLang } from '../siteLang';
import Footer from '../components/Footer';
import smiley from '../winston-smiley-white.svg';

// Refonte : abandon du chrome rétro Windows 95 pour la nouvelle esthétique —
// Helvetica Neue épaisse, noir/blanc/crème, zéro arrondi, boutons en texte nu.

const t = {
    fr: {
        windowTitle: 'winston.ie — Créer un compte',
        steps: ['accès', 'identité', 'compte'],
        inviteLabel: 'Clé d\'invitation',
        invitePlaceholder: 'Entre ta clé d\'invitation',
        inviteHint: 'Une clé d\'invitation est requise pour rejoindre l\'archive.',
        firstLabel: 'Prénom', firstPlaceholder: 'Prénom',
        lastLabel: 'Nom', lastPlaceholder: 'Nom',
        userLabel: 'Utilisateur', userPlaceholder: 'Ton identifiant',
        emailLabel: 'Email', emailPlaceholder: 'toi@example.com',
        passLabel: 'Mot de passe', passPlaceholder: '••••••••',
        continueBtn: 'Continuer →',
        joinBtn: 'Rejoindre →',
        creating: 'Création...',
        back: '← Retour',
        signInLink: '← Se connecter',
        errInviteRequired: 'Clé d\'invitation requise.',
        errInviteInvalid: 'Clé d\'invitation invalide.',
        errFieldsRequired: 'Tous les champs sont requis.',
        agreeText: 'J\'accepte les ',
        agreeLink: 'conditions et l\'entente contributeur',
        errAgreeRequired: 'Tu dois accepter les conditions pour continuer.',
        footer: 'sur invitation seulement · winston.ie',
        backHome: '← winston.ie',
    },
    en: {
        windowTitle: 'winston.ie — Create account',
        steps: ['access', 'identity', 'account'],
        inviteLabel: 'Invite key',
        invitePlaceholder: 'Enter your invite key',
        inviteHint: 'You need an invite key to join the archive.',
        firstLabel: 'First name', firstPlaceholder: 'First name',
        lastLabel: 'Last name', lastPlaceholder: 'Last name',
        userLabel: 'Username', userPlaceholder: 'Your handle',
        emailLabel: 'Email', emailPlaceholder: 'you@example.com',
        passLabel: 'Password', passPlaceholder: '••••••••',
        continueBtn: 'Continue →',
        joinBtn: 'Join the archive →',
        creating: 'Creating...',
        back: '← Back',
        signInLink: '← Sign in',
        errInviteRequired: 'Invite key required.',
        errInviteInvalid: 'Invalid invite key.',
        errFieldsRequired: 'All fields are required.',
        agreeText: 'I agree to the ',
        agreeLink: 'Terms & Contributor Agreement',
        errAgreeRequired: 'You must accept the terms to continue.',
        footer: 'invite-only · winston.ie',
        backHome: '← winston.ie',
    },
    vi: {
        windowTitle: 'winston.ie — Tạo tài khoản',
        steps: ['truy cập', 'danh tính', 'tài khoản'],
        inviteLabel: 'Mã mời',
        invitePlaceholder: 'Nhập mã mời của bạn',
        inviteHint: 'Bạn cần mã mời để tham gia kho lưu trữ.',
        firstLabel: 'Tên', firstPlaceholder: 'Tên',
        lastLabel: 'Họ', lastPlaceholder: 'Họ',
        userLabel: 'Tên người dùng', userPlaceholder: 'Tên định danh của bạn',
        emailLabel: 'Email', emailPlaceholder: 'ban@example.com',
        passLabel: 'Mật khẩu', passPlaceholder: '••••••••',
        continueBtn: 'Tiếp tục →',
        joinBtn: 'Tham gia →',
        creating: 'Đang tạo...',
        back: '← Quay lại',
        signInLink: '← Đăng nhập',
        errInviteRequired: 'Cần có mã mời.',
        errInviteInvalid: 'Mã mời không hợp lệ.',
        errFieldsRequired: 'Tất cả các trường đều bắt buộc.',
        agreeText: 'Tôi đồng ý với ',
        agreeLink: 'Điều khoản & Thỏa thuận cộng tác viên',
        errAgreeRequired: 'Bạn phải đồng ý với các điều khoản để tiếp tục.',
        footer: 'chỉ dành cho người được mời · winston.ie',
        backHome: '← winston.ie',
    },
};

const UI_FONT = "'Helvetica Neue', Arial, sans-serif";
const INK = '#0e1013';
const BG = '#faf9f6';
const BORDER = '#e5e5e0';
const MUTED = '#8a8a86';
const ERR_BG = '#fdf2f1';
const ERR_BORDER = '#e0a89f';
const ERR_TEXT = '#a3382f';

const fieldStyle = {
    width: '100%',
    border: `1px solid ${BORDER}`,
    padding: '11px 12px',
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: UI_FONT,
    color: INK,
    outline: 'none',
    boxSizing: 'border-box',
    background: '#fff',
};

const labelStyle = { display: 'block', fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: MUTED, marginBottom: '6px' };

function Signup() {
    const navigate = useNavigate();
    const { lang, cycleLang } = useLang();
    const c = t[lang] || t.fr;
    const title = c.windowTitle.replace('winston.ie — ', '');

    const [step, setStep] = useState(1);
    const [inviteKey, setInviteKey] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [agreed, setAgreed] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);

    const handleNext = async () => {
        setError('');
        if (step === 1) {
            if (!inviteKey) { setError(c.errInviteRequired); return; }
            const { data: valid } = await supabase.rpc('verify_invite_key', { p_key: inviteKey });
            if (!valid) { setError(c.errInviteInvalid); return; }
            setStep(2);
        } else if (step === 2) {
            if (!firstName || !lastName) { setError(c.errFieldsRequired); return; }
            setStep(3);
        }
    };

    const handleSignup = async () => {
        if (!username || !email || !password) { setError(c.errFieldsRequired); return; }
        if (!agreed) { setError(c.errAgreeRequired); return; }
        setLoading(true);
        setError('');

        const normalizedUsername = username.trim().toLowerCase();

        // Captured at the moment the account is created — this timestamp
        // is the official record of when the Contributor Agreement was accepted.
        const agreementAcceptedAt = new Date().toISOString();

        const { error: signupError } = await supabase.auth.signUp({ email, password });
        if (signupError) { setError(signupError.message); setLoading(false); return; }

        await supabase.rpc('create_user_profile', {
            p_username: normalizedUsername,
            p_email: email,
            p_first_name: firstName,
            p_last_name: lastName,
            p_display_name: `${firstName} ${lastName}`,
            p_agreement_accepted_at: agreementAcceptedAt,
        });

        setLoading(false);
        navigate('/login');
    };

    useEffect(() => { document.title = 'Sign up | winston.ie'; }, []);

    return (
        <>
            <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: BG, fontFamily: UI_FONT }}>
                <style>{`
                    .wlink { position: relative; display: inline-block; cursor: pointer; opacity: 0.88; transition: opacity .25s ease; }
                    .wlink:hover { opacity: 1; }
                    .wlink::after { content: ''; position: absolute; left: 0; right: 0; bottom: -3px; height: 1px; background: currentColor; transform: scaleX(0); transform-origin: left; transition: transform .35s cubic-bezier(.4,0,.2,1); }
                    .wlink:hover::after { transform: scaleX(1); }
                `}</style>

                <div style={{ padding: '24px 28px' }}>
                    <p onClick={() => navigate('/')} className="wlink" style={{ fontSize: '13px', fontWeight: 500, color: INK, margin: 0 }}>{c.backHome}</p>
                </div>

                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                    <div style={{ width: '100%', maxWidth: '420px', background: '#fff', border: `1px solid ${BORDER}`, padding: '40px 32px' }}>

                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                            <img src={smiley} alt="winston.ie" style={{ height: '32px', width: 'auto', filter: 'invert(1)' }} />
                            <button onClick={cycleLang} className="wlink" style={{ background: 'none', border: 'none', padding: 0, fontFamily: UI_FONT, fontWeight: 500, fontSize: '12px', color: MUTED, cursor: 'pointer' }}>
                                {lang.toUpperCase()}
                            </button>
                        </div>

                        <h1 style={{ fontFamily: UI_FONT, fontWeight: 700, fontSize: '22px', color: INK, margin: '0 0 22px', letterSpacing: '-0.01em' }}>{title}</h1>

                        {/* Indicateur d'étapes — segments pleins (noir) vs vides (bordés) */}
                        <div style={{ display: 'flex', gap: '6px', marginBottom: '24px' }}>
                            {c.steps.map((label, i) => (
                                <div key={i} style={{ flex: 1 }}>
                                    <div style={{ height: '3px', background: i < step ? INK : '#fff', border: `1px solid ${i < step ? INK : BORDER}` }} />
                                    <p style={{ fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', textAlign: 'center', margin: '6px 0 0', color: i < step ? INK : MUTED }}>
                                        {i + 1}. {label}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {step === 1 && (
                            <div style={{ marginBottom: '18px' }}>
                                <label style={labelStyle}>{c.inviteLabel}</label>
                                <input
                                    type="password"
                                    value={inviteKey}
                                    onChange={e => setInviteKey(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && handleNext()}
                                    placeholder={c.invitePlaceholder}
                                    autoFocus
                                    style={fieldStyle}
                                />
                                <p style={{ fontSize: '11px', fontWeight: 500, color: MUTED, marginTop: '10px' }}>{c.inviteHint}</p>
                            </div>
                        )}

                        {step === 2 && (
                            <div style={{ marginBottom: '18px' }}>
                                <label style={labelStyle}>{c.firstLabel}</label>
                                <div style={{ marginBottom: '14px' }}>
                                    <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleNext()} placeholder={c.firstPlaceholder} autoFocus style={fieldStyle} />
                                </div>
                                <label style={labelStyle}>{c.lastLabel}</label>
                                <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleNext()} placeholder={c.lastPlaceholder} style={fieldStyle} />
                            </div>
                        )}

                        {step === 3 && (
                            <div style={{ marginBottom: '18px' }}>
                                <label style={labelStyle}>{c.userLabel}</label>
                                <div style={{ marginBottom: '14px' }}>
                                    <input type="text" value={username} onChange={e => setUsername(e.target.value)} placeholder={c.userPlaceholder} autoFocus style={fieldStyle} />
                                </div>
                                <label style={labelStyle}>{c.emailLabel}</label>
                                <div style={{ marginBottom: '14px' }}>
                                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={c.emailPlaceholder} style={fieldStyle} />
                                </div>
                                <label style={labelStyle}>{c.passLabel}</label>
                                <input type="password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSignup()} placeholder={c.passPlaceholder} style={fieldStyle} />

                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '16px' }}>
                                    <input
                                        type="checkbox"
                                        id="agree-checkbox"
                                        checked={agreed}
                                        onChange={e => setAgreed(e.target.checked)}
                                        style={{ marginTop: '2px', cursor: 'pointer' }}
                                    />
                                    <label htmlFor="agree-checkbox" style={{ fontSize: '12px', fontWeight: 500, color: INK, lineHeight: '1.5', cursor: 'pointer' }}>
                                        {c.agreeText}
                                        <span
                                            onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}
                                            className="wlink"
                                            style={{ color: INK, fontWeight: 700 }}
                                        >
                                            {c.agreeLink}
                                        </span>
                                    </label>
                                </div>
                            </div>
                        )}

                        {error && (
                            <div style={{ background: ERR_BG, border: `1px solid ${ERR_BORDER}`, padding: '10px 12px', fontSize: '12px', fontWeight: 500, color: ERR_TEXT, marginBottom: '18px' }}>
                                {error}
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', alignItems: 'center' }}>
                            {step > 1 ? (
                                <button onClick={() => { setStep(s => s - 1); setError(''); }} className="wlink" style={{ background: 'none', border: 'none', padding: 0, fontFamily: UI_FONT, fontSize: '12.5px', fontWeight: 500, color: MUTED, cursor: 'pointer' }}>{c.back}</button>
                            ) : (
                                <button onClick={() => navigate('/login')} className="wlink" style={{ background: 'none', border: 'none', padding: 0, fontFamily: UI_FONT, fontSize: '12.5px', fontWeight: 500, color: MUTED, cursor: 'pointer' }}>{c.signInLink}</button>
                            )}
                            <button onClick={step === 3 ? handleSignup : handleNext} disabled={loading} className="wlink" style={{ background: 'none', border: 'none', padding: 0, fontFamily: UI_FONT, fontSize: '15px', fontWeight: 700, color: INK, cursor: 'pointer', opacity: loading ? 0.5 : undefined }}>
                                {loading ? c.creating : step === 3 ? c.joinBtn : c.continueBtn}
                            </button>
                        </div>

                        <div style={{ borderTop: `1px solid ${BORDER}`, marginTop: '24px', paddingTop: '14px' }}>
                            <p style={{ fontSize: '10.5px', fontWeight: 500, color: MUTED, textAlign: 'center', margin: 0 }}>{c.footer}</p>
                        </div>
                    </div>
                </div>

                <Footer variant="light" />
            </div>

            {showTermsModal && (
                <TermsModal
                    fullName={`${firstName} ${lastName}`}
                    onClose={() => setShowTermsModal(false)}
                />
            )}
        </>
    );
}

export default Signup;